import React, { useState, useEffect, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import { MessageSquare, User, GraduationCap, Briefcase, MapPin, Save, X, Megaphone, Trash2, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import useEnquiryStages from "../../hooks/useEnquiryStages";
import useEnquiryOwnership from "../../hooks/useEnquiryOwnership";
import { getCurrentUser } from "../../utils/auth";
import { PageHeader, AppCard, AppCardContent, AppCardHeader, AppCardTitle, AppButton, FormActionBar, PhoneInput, ResetButton, ConfirmModal } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import { useBranch } from "../../contexts/BranchContext";
import "./new-enquiry.scss";

const DEGREES = [
  "B.Tech",
  "M.Tech",
  "B.Sc",
  "M.Sc",
  "MBA",
  "Other",
];

// Statuses come from the institute's configured pipeline — see useEnquiryStages.

const defaultState = {
  fullName: "",
  mobile: "",
  email: "",
  course: "",
  otherCourse: "",
  referenceName: "",
  referenceNumber: "",
  experience: "",
  passoutYear: "",
  degree: "",
  otherDegree: "",
  educationStream: "",
  placement: "",
  status: "Enquired",
  referenceType: "",
  address: "",
  comments: "",
};

const EnquiryForm = ({
  mode = "add",            // 'add' or 'edit'
  initialData = defaultState,
  onCancel,
  onSubmitSuccess,         // Callback when form successfully submits
  title = "New Enquiry",   // Form title
  subtitle = "Add new student enquiry",
  isGlobal = false, // Flag to indicate if this is a global enquiry form
}) => {
  const navigate = useNavigate();
  const { stageNames } = useEnquiryStages();
  // Skipped on the public /enroll page, which renders this same form with no
  // logged-in admin behind it.
  const { canAssign } = useEnquiryOwnership({ enabled: !isGlobal });
  const [form, setForm] = useState(initialData);
  const [courses, setCourses] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingCourses, setFetchingCourses] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const { branches, isMultiBranch } = useBranch();

  // Follow-up notes state (edit mode only)
  const [notes, setNotes] = useState(() => {
    if (initialData?.followUpNotes?.length) {
      return [...initialData.followUpNotes].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    return [];
  });
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState(null);

  // Sync form & notes when initialData changes (e.g. parent fetches fresh data)
  useEffect(() => {
    setForm(initialData);
    const sorted = initialData?.followUpNotes?.length
      ? [...initialData.followUpNotes].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      : [];
    setNotes(sorted);
  }, [initialData]);

  useEffect(() => {
    const fetchCourses = async () => {
      setFetchingCourses(true);
      try {
        const { data } = await apiClient.get("/courses/list");
        setCourses(data.courses || []);
      } catch {
        setToast({ show: true, message: "Failed to load course list.", type: "error" });
      }
      setFetchingCourses(false);
    };
    fetchCourses();

    // Fetch admins for assignee picker (silent fail; field is optional)
    apiClient.get("/admins/list")
      .then(({ data }) => setAdmins(data.admins || []))
      .catch(() => setAdmins([]));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...form, [name]: value };
    // Clear otherCourse when switching away from "others"
    if (name === "course" && value !== "others") updated.otherCourse = "";
    // Clear otherDegree when switching away from "Other"
    if (name === "degree" && value !== "Other") updated.otherDegree = "";
    setForm(updated);
    setToast((t) => ({ ...t, show: false }));
  };

  const handleRadio = (e) => {
    setForm((prev) => ({ ...prev, placement: e.target.value }));
    setToast((t) => ({ ...t, show: false }));
  };

  // Follow-up notes handlers
  const handleAddNote = async () => {
    if (!newNote.trim() || !form._id) return;
    setAddingNote(true);
    try {
      const user = getCurrentUser();
      const { data } = await apiClient.post(`/enquiries/${form._id}/notes`, {
        note: newNote.trim(),
        createdBy: user?.name || "Admin",
      });
      const sorted = [...(data.enquiry.followUpNotes || [])].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setNotes(sorted);
      setNewNote("");
      setToast({ show: true, message: "Follow-up note added.", type: "success" });
    } catch {
      setToast({ show: true, message: "Failed to add note.", type: "error" });
    }
    setAddingNote(false);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const { data } = await apiClient.delete(`/enquiries/${form._id}/notes/${noteId}`);
      const sorted = [...(data.enquiry.followUpNotes || [])].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setNotes(sorted);
      setToast({ show: true, message: "Note deleted.", type: "success" });
    } catch {
      setToast({ show: true, message: "Failed to delete note.", type: "error" });
    }
    setDeleteNoteId(null);
  };

  // Group notes by date
  const groupedNotes = notes.reduce((acc, note) => {
    const dateKey = new Date(note.createdAt).toLocaleDateString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(note);
    return acc;
  }, {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setToast({ show: false, message: "", type: "success" });
    try {
      // `course` is an ObjectId ref on the server; "others" and "" are both
      // "no linked course", and the name lives in otherCourse. In edit mode it
      // arrives populated, so send the id rather than the whole course object.
      const courseRef = form.course?._id || form.course;
      const payload = { ...form, course: !courseRef || courseRef === "others" ? null : courseRef };
      if (mode === "add") {
        await apiClient.post("/enquiries/add", payload);
        setToast({ show: true, message: "Enquiry submitted successfully!", type: "success" });
        setForm(defaultState);
      } else {
        await apiClient.put(`/enquiries/${form._id}`, payload);
        setToast({ show: true, message: "Enquiry updated successfully!", type: "success" });
      }
      if (onSubmitSuccess) onSubmitSuccess(form);
    } catch (err) {
      setToast({ show: true, message: `Failed to ${mode === "add" ? "submit" : "update"} enquiry. Please try again.`, type: "error" });
    }
    setLoading(false);
  };

  const handleReset = () => {
    setForm(initialData);
    setToast((t) => ({ ...t, show: false }));
  };

  // The list from /courses/list mixes both delivery models, so a flat dropdown
  // reads as one undifferentiated pile. Bucket them under optgroup headings.
  // "Live" is everything that is not self-paced — that matches the backend's
  // own definition of the batch filter (`courseType: { $ne: 'self-paced' }`),
  // so legacy rows saved before courseType existed stay where they belong.
  // Sorted by name within a group: grouping already discards the endpoint's
  // createdAt order, and a picker this long is scanned by name.
  const courseGroups = useMemo(() => {
    const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
    return [
      { label: "Live Courses", items: courses.filter((c) => c.courseType !== "self-paced").sort(byName) },
      { label: "Self-Paced Courses", items: courses.filter((c) => c.courseType === "self-paced").sort(byName) },
    ].filter((g) => g.items.length > 0); // an empty optgroup renders as a stray heading
  }, [courses]);

  // "Other Course" is a real selection, not the absence of one: it must come
  // from `form.course === "others"` (or, when editing a lead saved that way,
  // from the typed name in `otherCourse`). Deriving it from an empty `course`
  // showed "Other Course" on a blank add form while state still held "", so the
  // required-select passed, the typed name was posted alongside `course: ""`,
  // and the save died on an ObjectId cast.
  const courseValue = form.course
    ? (form.course._id || form.course)
    : (form.otherCourse ? "others" : "");
  const isOtherCourse = courseValue === "others";
  const isOtherDegree = form.degree === "Other";

  return (
    <div className={`eq-form-page${!isGlobal ? " has-form-actionbar" : ""}`}>
      {/* Page Header */}
      {!isGlobal && (
        <PageHeader
          backButton
          onBack={onCancel || (() => navigate(-1))}
          icon={MessageSquare}
          title={title}
          description={subtitle}
        />
      )}

      <form onSubmit={handleSubmit}>
        <div className="eq-form-layout">
          {/* ── Left Column ── */}
          <div className="eq-form-col">
            {/* Personal Information */}
            <AppCard>
              <AppCardHeader>
                <AppCardTitle>
                  <span className="eq-section-title"><User size={16} /> Personal Information</span>
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <div className="eq-field-grid">
                  <div className="eq-field">
                    <label htmlFor="eq-fullName">Full Name <span className="eq-required">*</span></label>
                    <input
                      id="eq-fullName"
                      name="fullName"
                      placeholder="Enter full name"
                      value={form.fullName}
                      onChange={handleChange}
                      autoComplete="off"
                      required
                    />
                  </div>
                  {/* The control sits outside .eq-field: its `input, select`
                      rules (0,1,1) would out-specify PhoneInput's own. */}
                  <div className="eq-field-stack">
                    <div className="eq-field">
                      <label htmlFor="eq-mobile">Mobile Number <span className="eq-required">*</span></label>
                    </div>
                    <PhoneInput
                      id="eq-mobile"
                      name="mobile"
                      placeholder="Enter mobile number"
                      value={form.mobile}
                      onChange={(mobile) => {
                        setForm((prev) => ({ ...prev, mobile }));
                        setToast((t) => ({ ...t, show: false }));
                      }}
                      autoComplete="off"
                      required
                    />
                  </div>
                  <div className="eq-field">
                    <label htmlFor="eq-email">Email Address <span className="eq-required">*</span></label>
                    <input
                      id="eq-email"
                      name="email"
                      placeholder="Enter email address"
                      value={form.email}
                      onChange={handleChange}
                      autoComplete="off"
                      type="email"
                      required
                    />
                  </div>
                  <div className="eq-field">
                    <label htmlFor="eq-address">Address</label>
                    <input
                      id="eq-address"
                      name="address"
                      placeholder="Enter address"
                      value={form.address}
                      onChange={handleChange}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </AppCardContent>
            </AppCard>

            {/* Education Details */}
            <AppCard>
              <AppCardHeader>
                <AppCardTitle>
                  <span className="eq-section-title"><GraduationCap size={16} /> Education Details</span>
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <div className="eq-field-grid">
                  <div className="eq-field">
                    <label htmlFor="eq-degree">Degree Type</label>
                    <select
                      id="eq-degree"
                      name="degree"
                      value={form.degree}
                      onChange={handleChange}
                    >
                      <option value="">Select Degree</option>
                      {DEGREES.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                  {isOtherDegree && (
                    <div className="eq-field">
                      <label htmlFor="eq-otherDegree">Degree Name <span className="eq-required">*</span></label>
                      <input
                        id="eq-otherDegree"
                        name="otherDegree"
                        placeholder="Enter degree name"
                        value={form.otherDegree}
                        onChange={handleChange}
                        autoComplete="off"
                        required
                      />
                    </div>
                  )}
                  <div className="eq-field">
                    <label htmlFor="eq-educationStream">Education Stream</label>
                    <input
                      id="eq-educationStream"
                      name="educationStream"
                      placeholder="Ex: Computer Science, Electronics"
                      value={form.educationStream}
                      onChange={handleChange}
                      autoComplete="off"
                    />
                  </div>
                  <div className="eq-field">
                    <label htmlFor="eq-passoutYear">Passed Out Year</label>
                    <input
                      id="eq-passoutYear"
                      name="passoutYear"
                      placeholder="Enter passed out year"
                      value={form.passoutYear}
                      onChange={handleChange}
                      autoComplete="off"
                    />
                  </div>
                  <div className="eq-field">
                    <label htmlFor="eq-experience">Experience (Years)</label>
                    <input
                      id="eq-experience"
                      name="experience"
                      placeholder="Enter years of experience"
                      value={form.experience}
                      onChange={handleChange}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </AppCardContent>
            </AppCard>
          </div>

          {/* ── Right Column ── */}
          <div className="eq-form-col">
            {/* Course & Status */}
            <AppCard>
              <AppCardHeader>
                <AppCardTitle>
                  <span className="eq-section-title"><Briefcase size={16} /> Course & Status</span>
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <div className="eq-field-grid">
                  <div className="eq-field">
                    <label htmlFor="eq-course">Course Interested <span className="eq-required">*</span></label>
                    <select
                      id="eq-course"
                      name="course"
                      value={courseValue}
                      onChange={handleChange}
                      required
                      disabled={fetchingCourses}
                    >
                      <option value="">
                        {fetchingCourses ? "Loading courses..." : "Select Course"}
                      </option>
                      {courseGroups.map((g) => (
                        <optgroup key={g.label} label={g.label}>
                          {g.items.map((c) => (
                            <option key={c._id} value={c._id}>
                              {c.name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                      <optgroup label="Other">
                        <option value="others">Other Course</option>
                      </optgroup>
                    </select>
                  </div>
                  {isOtherCourse && (
                    <div className="eq-field">
                      <label htmlFor="eq-otherCourse">Course Name <span className="eq-required">*</span></label>
                      <input
                        id="eq-otherCourse"
                        name="otherCourse"
                        placeholder="Enter course name"
                        value={form.otherCourse}
                        onChange={handleChange}
                        autoComplete="off"
                        required
                      />
                    </div>
                  )}
                  {!isGlobal && (
                    <div className="eq-field">
                      <label htmlFor="eq-status">Status <span className="eq-required">*</span></label>
                      <select
                        id="eq-status"
                        name="status"
                        value={form.status}
                        onChange={handleChange}
                        required
                      >
                        {stageNames.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {isMultiBranch && branches.length > 0 && !isGlobal && (
                    <div className="eq-field">
                      <label htmlFor="eq-branchId">Branch</label>
                      <select
                        id="eq-branchId"
                        name="branchId"
                        value={form.branchId?._id || form.branchId || ""}
                        onChange={handleChange}
                      >
                        <option value="">No branch</option>
                        {branches.filter(b => b.isActive).map(b => (
                          <option key={b._id} value={b._id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {/* Under Enquiry Ownership Control the assignee is not the
                      author's to choose: the server assigns a new lead to
                      whoever created it and ignores any assignee sent on an
                      edit. A picker here would look like a decision and take
                      effect as nothing. */}
                  {!isGlobal && canAssign && (
                    <div className="eq-field">
                      <label htmlFor="eq-assignedTo">Assigned To</label>
                      <select
                        id="eq-assignedTo"
                        name="assignedTo"
                        value={form.assignedTo?._id || form.assignedTo || ""}
                        onChange={handleChange}
                      >
                        <option value="">Unassigned</option>
                        {admins.map((a) => {
                          const name = `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.email || "Admin";
                          return (
                            <option key={a._id} value={a._id}>{name}{a.email && a.email !== name ? ` (${a.email})` : ""}</option>
                          );
                        })}
                      </select>
                    </div>
                  )}
                  <div className="eq-field">
                    <label>Placement Required</label>
                    <div className="eq-radio-group">
                      <label className="eq-radio-label">
                        <input
                          type="radio"
                          name="placement"
                          value="Yes"
                          checked={form.placement === "Yes"}
                          onChange={handleRadio}
                        />
                        <span>Yes</span>
                      </label>
                      <label className="eq-radio-label">
                        <input
                          type="radio"
                          name="placement"
                          value="No"
                          checked={form.placement === "No"}
                          onChange={handleRadio}
                        />
                        <span>No</span>
                      </label>
                    </div>
                  </div>
                </div>
              </AppCardContent>
            </AppCard>

            {/* Reference & Additional Info */}
            <AppCard>
              <AppCardHeader>
                <AppCardTitle>
                  <span className="eq-section-title"><MapPin size={16} /> Reference & Additional</span>
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <div className="eq-field-grid">
                  <div className="eq-field eq-field--full">
                    <label htmlFor="eq-referenceType">How did you know about us?</label>
                    <select
                      id="eq-referenceType"
                      name="referenceType"
                      value={form.referenceType}
                      onChange={handleChange}
                    >
                      <option value="">Select Reference</option>
                      <option value="Friend">Friend / Family</option>
                      <option value="Social Media">Social Media</option>
                      <option value="Google">Google</option>
                      <option value="Advertisement">Advertisement</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                  {form.referenceType === "Friend" && (
                    <>
                      <div className="eq-field">
                        <label htmlFor="eq-referenceName">Reference Name</label>
                        <input
                          id="eq-referenceName"
                          name="referenceName"
                          placeholder="Enter reference name"
                          value={form.referenceName}
                          onChange={handleChange}
                          autoComplete="off"
                        />
                      </div>
                      <div className="eq-field">
                        <label htmlFor="eq-referenceNumber">Reference Mobile</label>
                        <input
                          id="eq-referenceNumber"
                          name="referenceNumber"
                          placeholder="Enter reference number"
                          value={form.referenceNumber}
                          onChange={handleChange}
                          autoComplete="off"
                        />
                      </div>
                    </>
                  )}
                  <div className="eq-field eq-field--full">
                    <label htmlFor="eq-comments">Additional Comments</label>
                    <textarea
                      id="eq-comments"
                      name="comments"
                      rows={3}
                      placeholder="Enter any additional comments or requirements"
                      value={form.comments}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </AppCardContent>
            </AppCard>
          </div>
        </div>

        {/* ── Lead Source / Campaign Info (edit mode, only if external lead) ── */}
        {mode === "edit" && (form.leadSource && form.leadSource !== "manual") && (
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>
                <span className="eq-section-title">
                  <Megaphone size={14} /> Lead Source &amp; Campaign
                </span>
              </AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="eq-field-grid">
                <div className="eq-field">
                  <label>Source</label>
                  <input
                    value={
                      ({
                        google_ads: "Google Ads",
                        meta_ads: "Meta Ads",
                        whatsapp: "WhatsApp",
                        website: "Website",
                        embed_form: "Embed Form",
                        zoho_crm: "Zoho CRM",
                        other: "Other",
                      })[form.leadSource] || form.leadSource
                    }
                    readOnly
                    className="form-control-plaintext"
                  />
                </div>
                {form.externalLeadId && (
                  <div className="eq-field">
                    <label>External Lead ID</label>
                    <input value={form.externalLeadId} readOnly className="form-control-plaintext" />
                  </div>
                )}
                {form.campaign?.name && (
                  <div className="eq-field">
                    <label>Campaign</label>
                    <input value={form.campaign.name} readOnly className="form-control-plaintext" />
                  </div>
                )}
                {form.campaign?.adGroupName && (
                  <div className="eq-field">
                    <label>Ad Group</label>
                    <input value={form.campaign.adGroupName} readOnly className="form-control-plaintext" />
                  </div>
                )}
                {form.campaign?.formId && (
                  <div className="eq-field">
                    <label>Form ID</label>
                    <input value={form.campaign.formId} readOnly className="form-control-plaintext" />
                  </div>
                )}
                {form.campaign?.creativeId && (
                  <div className="eq-field">
                    <label>Creative ID</label>
                    <input value={form.campaign.creativeId} readOnly className="form-control-plaintext" />
                  </div>
                )}
                {form.utm?.source && (
                  <div className="eq-field">
                    <label>UTM Source / Medium</label>
                    <input
                      value={[form.utm.source, form.utm.medium].filter(Boolean).join(" / ")}
                      readOnly
                      className="form-control-plaintext"
                    />
                  </div>
                )}
              </div>

              {/* Custom fields — anything the adapter put in customFields */}
              {form.customFields && Object.keys(form.customFields).length > 0 && (
                <>
                  <hr />
                  <label style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Additional Fields
                  </label>
                  <div className="eq-field-grid" style={{ marginTop: 8 }}>
                    {Object.entries(form.customFields).map(([key, value]) => (
                      <div className="eq-field" key={key}>
                        <label style={{ textTransform: "capitalize" }}>
                          {key.replace(/_/g, " ")}
                        </label>
                        <input
                          value={
                            value === null || value === undefined
                              ? "—"
                              : typeof value === "object"
                                ? JSON.stringify(value)
                                : String(value)
                          }
                          readOnly
                          className="form-control-plaintext"
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </AppCardContent>
          </AppCard>
        )}

        {/* ── Zoho CRM Info (edit mode, only if linked) ── */}
        {mode === "edit" && form.zohoLeadId && (
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>
                <span className="eq-section-title">
                  <Briefcase size={14} /> Zoho CRM
                </span>
              </AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="eq-field-grid">
                <div className="eq-field">
                  <label>Zoho Lead ID</label>
                  <input value={form.zohoLeadId} readOnly className="form-control-plaintext" />
                </div>
                <div className="eq-field">
                  <label>Sync Status</label>
                  <span className={`badge ${
                    form.zohoSyncStatus === "synced" ? "bg-success" :
                    form.zohoSyncStatus === "pending" ? "bg-warning text-dark" :
                    form.zohoSyncStatus === "failed" ? "bg-danger" : "bg-secondary"
                  }`}>
                    {form.zohoSyncStatus || "Not synced"}
                  </span>
                </div>
                {form.zohoLastSyncedAt && (
                  <div className="eq-field">
                    <label>Last Synced</label>
                    <span className="text-muted">
                      {new Date(form.zohoLastSyncedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                    </span>
                  </div>
                )}
              </div>
            </AppCardContent>
          </AppCard>
        )}

        {/* ── Follow-up Notes (edit mode only) ── */}
        {mode === "edit" && form._id && (
          <AppCard className="eq-notes-card">
            <AppCardHeader>
              <AppCardTitle>
                <span className="eq-section-title">
                  <Clock size={14} /> Follow-up Notes
                  {notes.length > 0 && <span className="eq-notes-count">{notes.length}</span>}
                </span>
              </AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              {/* Add new note */}
              <div className="eq-note-input-row">
                <div className="eq-note-avatar">
                  <User size={10} />
                </div>
                <div className="eq-note-input-wrap">
                  <textarea
                    rows={1}
                    placeholder="Write a follow-up note…"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="eq-note-input"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAddNote();
                      }
                    }}
                  />
                  {newNote.trim() && (
                    <AppButton
                      size="sm"
                      className="eq-note-add-btn"
                      type="button"
                      onClick={handleAddNote}
                      disabled={addingNote}
                    >
                      {addingNote ? <Spinner size="sm" /> : "Add Note"}
                    </AppButton>
                  )}
                </div>
              </div>

              {/* Notes timeline */}
              {Object.keys(groupedNotes).length > 0 ? (
                <div className="eq-notes-timeline">
                  {Object.entries(groupedNotes).map(([dateLabel, dateNotes]) => (
                    <div key={dateLabel} className="eq-notes-day-group">
                      <div className="eq-notes-date-label">{dateLabel}</div>
                      {dateNotes.map((n) => (
                        <div key={n._id} className="eq-note-item">
                          <div className="eq-note-dot" />
                          <div className="eq-note-content">
                            <div className="eq-note-topline">
                              <span className="eq-note-author">{n.createdBy || "Admin"}</span>
                              <span className="eq-note-time">
                                {new Date(n.createdAt).toLocaleTimeString("en-IN", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  timeZone: "Asia/Kolkata",
                                })}
                              </span>
                              <button
                                type="button"
                                className="eq-note-delete-btn"
                                onClick={() => setDeleteNoteId(n._id)}
                                title="Delete note"
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                            <div className="eq-note-body">{n.note}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="eq-notes-empty">
                  <Clock size={16} />
                  <span>No follow-up notes yet</span>
                </div>
              )}
            </AppCardContent>
          </AppCard>
        )}

        {/* ── Action Buttons ── */}
        {/* Global (public EnrollPage) has no sidebar, so the fixed bar would
            misalign — keep inline actions there; use the fixed bar elsewhere. */}
        {isGlobal ? (
          <div className="eq-form-actions">
            <AppButton type="submit" disabled={loading || fetchingCourses}>
              {loading ? <Spinner size="sm" /> : <><Save size={16} /> {mode === "edit" ? "Update Enquiry" : "Submit Enquiry"}</>}
            </AppButton>
            <AppButton variant="outline" type="button" onClick={onCancel || (() => navigate(-1))}>
              <X size={16} /> Cancel
            </AppButton>
          </div>
        ) : (
          <FormActionBar>
            <AppButton variant="outline" type="button" onClick={onCancel || (() => navigate(-1))}>
              <X size={16} /> Cancel
            </AppButton>
            <ResetButton onReset={handleReset} />
            <AppButton type="submit" disabled={loading || fetchingCourses}>
              {loading ? <Spinner size="sm" /> : <><Save size={16} /> {mode === "edit" ? "Update Enquiry" : "Submit Enquiry"}</>}
            </AppButton>
          </FormActionBar>
        )}
      </form>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
        position="top-end"
      />

      <ConfirmModal
        show={!!deleteNoteId}
        onHide={() => setDeleteNoteId(null)}
        onConfirm={() => handleDeleteNote(deleteNoteId)}
        title="Delete Note"
        message="Are you sure you want to delete this follow-up note?"
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default EnquiryForm;
